import React, { useCallback, useState } from "react";
import { Drawer, Button, Select, Space } from "antd";
import type { PageSizeType, Templates } from "../templates";
import { useKeyboardShortcuts } from "../../Hook";
import { SHORTCUTS } from "../../Constant";
import { useInvoiceData } from "./useInvoiceData";
import { useInvoiceDocument } from "./useInvoiceDocument";
import { DRAWER_FORM_WIDTH, drawerTitle } from "../AppUi";

interface InvoiceModalProps {
  open: boolean;
  orderId: string | null;
  onClose: () => void;
}

const PAGE_SIZE_OPTIONS = [
  { value: "A4", label: "A4 Paper" },
  { value: "A3", label: "A3 Paper" },
  { value: "80mm", label: "80mm POS" },
] satisfies Array<{ value: PageSizeType; label: string }>;

export const InvoiceModal: React.FC<InvoiceModalProps> = ({
  open,
  orderId,
  onClose,
}) => {
  const [pageSize, setPageSize] = useState<PageSizeType>("80mm");
  const [template, setTemplate] = useState<Templates>("classic");

  const { order, config } = useInvoiceData({ open, orderId });
  const { getInvoiceHtml, printInvoice, downloadInvoice } = useInvoiceDocument({
    order,
    config,
    pageSize,
    template,
  });

  const cycleTemplate = useCallback(() => {
    setTemplate((prev) => (prev === "classic" ? "corporate" : "classic"));
  }, []);

  const cyclePageSize = useCallback(() => {
    setPageSize((prev) => {
      if (prev === "A4") return "A3";
      if (prev === "A3") return "80mm";
      return "A4";
    });
  }, []);

  useKeyboardShortcuts([
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.PRINT_INVOICE,
      options: { enabled: open },
      handler: printInvoice,
    },
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.DOWNLOAD_PDF,
      options: { enabled: open },
      handler: downloadInvoice,
    },
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.CHANGE_TEMPLATE,
      options: { enabled: open },
      handler: cycleTemplate,
    },
    {
      shortcut: SHORTCUTS.INVOICE_PRINT.CHANGE_PAGE_SIZE,
      options: { enabled: open },
      handler: cyclePageSize,
    },
  ]);

  if (!order) return null;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={drawerTitle("Invoice Preview")}
      width={DRAWER_FORM_WIDTH}
      className="invoice-drawer"
      destroyOnClose
      footer={
        <div className="flex flex-col gap-2">
          <div className="flex justify-end gap-2">
            <Space>
              <Button
                type="primary"
                size="middle"
                onClick={printInvoice}
                id="print_invoice_pdf"
                className="text-xs font-semibold h-9 rounded-md"
              >
                Print Invoice
              </Button>
              <Button
                size="middle"
                onClick={downloadInvoice}
                id="download_invoice_pdf"
                className="text-xs font-semibold h-9 rounded-md"
              >
                Download
              </Button>
            </Space>
          </div>
          <div className="flex justify-between items-center text-[10px] text-gray-400 px-1 border-t border-gray-150 pt-1.5 mt-0.5">
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-150 border border-gray-200 rounded text-gray-500 font-mono text-[9px] leading-none">
                {SHORTCUTS.INVOICE_PRINT.PRINT_INVOICE.name}
              </kbd>
              Print
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-150 border border-gray-200 rounded text-gray-500 font-mono text-[9px] leading-none">
                {SHORTCUTS.INVOICE_PRINT.DOWNLOAD_PDF.name}
              </kbd>
              Download PDF
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-150 border border-gray-200 rounded text-gray-500 font-mono text-[9px] leading-none">
                {SHORTCUTS.INVOICE_PRINT.CHANGE_TEMPLATE.name}
              </kbd>
              Cycle Template
            </span>
            <span className="flex items-center gap-1">
              <kbd className="px-1 py-0.5 bg-gray-150 border border-gray-200 rounded text-gray-500 font-mono text-[9px] leading-none">
                {SHORTCUTS.INVOICE_PRINT.CHANGE_PAGE_SIZE.name}
              </kbd>
              Cycle Size
            </span>
          </div>
        </div>
      }
    >
      <>
        <div className="flex gap-5 justify-center">
          <Select
            size="middle"
            className="w-32"
            value={template}
            onChange={(e) => setTemplate(e)}
          >
            <Select.Option value="classic">Classic</Select.Option>
            <Select.Option value="corporate">Corporate</Select.Option>
          </Select>
          <Select
            size="middle"
            className="w-32"
            value={pageSize}
            onChange={(v) => setPageSize(v)}
            options={PAGE_SIZE_OPTIONS}
          />
        </div>
        <div
          dangerouslySetInnerHTML={{ __html: getInvoiceHtml(false) }}
          style={{ width: "100%" }}
        />
      </>
    </Drawer>
  );
};
